import AccountApi from '../../aws/account/AccountApi'
import EThreeExtender from "./EThreeExtender";
import E2EECardCache from "./E2EECardCache";
import E2EEStatus from "./E2EEStatus";
import E2EEEvictionTracker from "./E2EEEvictionTracker";
import { E2EEPasscodeRequiredError, E2EESenderCardError } from "./E2EEErrors";
import * as Nc1Decryptor from "./nc1/Decryptor";
import * as DecryptTiming from "./DecryptTiming";

const INIT_TIMEOUT_MS = 120 * 1000
const DECRYPT_TIMEOUT_MS = 120 * 1000

const accountCardFilter = (card) => {
  // console.log(JSON.stringify(card))
  if(!card.isOutdated) {
    for(let signature of card.signatures) {
      if(signature.extraFields && signature.extraFields.accountCard === "true") {
        // console.log('true')
        return true
      }
    }
  }
  // console.log('false')
  return false
}

//const isWebAssemblySupported = false; //e3kit appears to have a webassembly bug in v2.3.2, undo this
const isWebAssemblySupported = (() => {
  try {
    if (typeof WebAssembly === "object"
      && typeof WebAssembly.instantiate === "function") {
      const module = new WebAssembly.Module(Uint8Array.of(0x0, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00));
      if (module instanceof WebAssembly.Module)
        return new WebAssembly.Instance(module) instanceof WebAssembly.Instance;
    }
  } catch (e) {
    console.log(e)
  }
  return false;
})();

class E2EEHelper {

  constructor(forAccountId) {
    this.accountId = forAccountId
    this.virgilAccountIdentityId = null
    this.eThree = null
    // N11 (2026-09-16): the legacy (Virgil) init is LAZY. Constructing a
    // helper is free — nothing is imported or contacted until a caller needs
    // the legacy scheme (a non-NC1 record, the legacy management UI, a card
    // revoke). Before this, every dashboard load ran the e3kit import +
    // EThree.initialize + a Virgil card lookup for the logged-in account AND
    // every viewed account, whatever their scheme.
    this._initPromise = null
    this._resolveInitialized = null
    this._initialized = new Promise((resolve) => { this._resolveInitialized = resolve })
    this.e2eeEnabled = null
    this.hasLocalKey = null
    this.bypassPrivateKeyMissing = false
    this.jwt = null

    this.maxTimestampWithSucccessfulDecryption = 0
    this.lastRefreshedCardToVerifyCard = 0
  }

  /** The Virgil init has been started (in flight, done, or failed). */
  get initStarted() { return this._initPromise !== null }

  /** The Virgil init completed successfully. */
  get isInitialized() { return !!this.eThree }

  /**
   * Start the legacy init on first use and return its promise. Every method
   * that needs Virgil awaits this (through the `initPromise` getter below);
   * NC1 records are dispatched before it is ever reached. A failed init stays
   * failed — callers surface the error — until probeConnection() re-runs it,
   * exactly as in the eager era.
   */
  ensureInit() {
    if(!this._initPromise) {
      this._initPromise = this.init(this.accountId)
    }
    return this._initPromise
  }

  /**
   * Reading `initPromise` STARTS the init. Kept as the public surface for the
   * legacy pages that `await helper.initPromise` (management UI, device-card
   * revocation, account deletion, debug panels): they want Virgil, so touching
   * it is the right trigger. The helper's own methods rely on it too.
   */
  get initPromise() { return this.ensureInit() }

  /**
   * Resolves once Virgil HAS initialized, without starting it — for observers
   * that only care about legacy state if something else needed Virgil anyway
   * (HomeV2.runE2EEFix). Never resolves if nothing triggers the init.
   */
  whenInitialized() { return this._initialized }

  _tokenCallback(forAccountId) {
    return async () => {
      let response = await AccountApi.getJwtToken(forAccountId)

      if(!response || !response.data) {
        console.log(response)
        alert(`An issue occurred.  We'll reload the page.  If this repeats, contact support@${process.env.VUE_APP_DOMAIN}`)
        location.reload()
      }

      this.virgilAccountIdentityId = response.data.altAccountId
      this.jwt = response.data.jwt
      return response.data.jwt
    }
  }

  async revokeCardsForDeletedDevices(set) {
    await this.initPromise
    return await this.eThree.revokeCardsForDeletedDevices(set)
  }

  async probeConnection() {
    if(!this.eThree) {
      // Re-run a failed (or never-run) init — the only retry path.
      this._initPromise = this.init(this.accountId)
      await this._initPromise
      return
    }
    try {
      await E2EEStatus.withSlowWarning(
        this.eThree.findUsersWithCardFilter(this.virgilAccountIdentityId, () => false),
        10 * 1000,
        'probeConnection'
      )
    } catch(e) {
      if(e && e.name === 'UsersNotFoundError') {
        return
      }
      throw e
    }
    E2EEStatus.reportSuccess()
  }

  async getWebWorkerE2EEConstructionObject() {
    // let privateKey = await this.eThree.virgilCrypto.exportPrivateKey()
    return {
      jwt: this.jwt,
      virgilAccountIdentityId: this.virgilAccountIdentityId,
      accountId: this.accountId
    }
  }

  async init(forAccountId) {
    console.log('init started')
    try {
      let e3Kit = null
      if(isWebAssemblySupported) {
        try {
          e3Kit = await import('@virgilsecurity/e3kit-browser')
          // console.log('using web assembly')
        } catch(e) {
          console.log(e)
        }
      }
      if(!e3Kit) {
        console.log('failed to load wasm, loading asmjs')
        e3Kit = await import('@virgilsecurity/e3kit-browser/dist/browser.asmjs.es')
        console.log('using asmjs')
      }

      let originalEThree = await E2EEStatus.withSlowWarning(
        e3Kit.EThree.initialize(this._tokenCallback(forAccountId)),
        INIT_TIMEOUT_MS,
        'EThree.initialize'
      )
      this.eThree = EThreeExtender.extend(originalEThree)
      // console.log('eThree loaded')

      this.e2eeEnabled = await E2EEStatus.withSlowWarning(
        this.hasE2eEnabled(),
        INIT_TIMEOUT_MS,
        'hasE2eEnabled'
      )
      this.hasLocalKey = await E2EEStatus.withSlowWarning(
        this._hasPrivateKeyThatIsValid(),
        INIT_TIMEOUT_MS,
        '_hasPrivateKeyThatIsValid'
      )

      E2EEStatus.reportSuccess()
      this._resolveInitialized()
    } catch(e) {
      console.log('E2EEHelper.init failed')
      console.log(e)
      E2EEStatus.reportConnectionIssue(`init error: ${e && (e.message || e.name) || 'unknown'}`)
      throw e
    }
  }

  _getCachedAccountCardId() {
    return localStorage.getItem(`${this.accountId}|${this.virgilAccountIdentityId}|account.cardId`)
  }
  _setCachedAccountCardId(value) {
    localStorage.setItem(`${this.accountId}|${this.virgilAccountIdentityId}|account.cardId`, value)
  }

  async _hasPrivateKeyThatIsValid() {
    console.log('__hasPrivateKeyThatIsValid')
    let hasPrivateKey = await this.eThree.hasLocalPrivateKey()
    console.log('__hasPrivateKeyThatIsValid done')

    if(hasPrivateKey) {
      console.log('hasPrivateKey')
      let storedCardId = this._getCachedAccountCardId()
      console.log('_getCachedAccountCardId done')
      try {
        let cloudCardId = await this._getAccountCardId()
        console.log('hasPrivateKey done')
        if(storedCardId === cloudCardId) {
          return true
        }
      } catch(e) {

        //account must have subscribed then cancelled, with the private key still present on the browser storage but no longer valid.  Lets clean it up.
        console.log('e2ee init mismatch: ' + (e && (e.name || e.message) || 'unknown'))
        await this.eThree.cleanup().catch(err => {
          console.log('error cleanup')
          console.log(err)
        })
      }
    }

    return false
  }

  // Resolve the sending device's Virgil card, translating failures into typed
  // errors so callers/UI can classify them. Never returns a falsy value — a
  // missing card or lookup failure throws rather than being handed to Virgil
  // (which would report the opaque "...Got boolean" error).
  async _resolveSendersCard(identityId, label) {
    let card
    try {
      card = await E2EEStatus.withSlowWarning(
        E2EECardCache.getSendersCard(this, identityId),
        DECRYPT_TIMEOUT_MS,
        label
      )
    } catch(e) {
      throw new E2EESenderCardError(`Unable to load encryption key for ${identityId}`, e)
    }
    if(!card) {
      throw new E2EESenderCardError(`No encryption key found for ${identityId}`)
    }
    return card
  }

  // Dev/debug only: times every decrypt so the two schemes can be compared
  // (DecryptTiming.js; `timing` is null on a normal prod session).
  // `imageKind` ('thumbnail' | 'fullsize', optional) only labels the timing sample.
  async decryptImage(identityId, blob, imageTimestamp, imageKind) {
    const timing = DecryptTiming.start(imageKind === 'thumbnail' || imageKind === 'fullsize' ? imageKind : 'image')
    try {
      const ret = await this._decryptImage(identityId, blob, imageTimestamp, timing)
      if(timing) timing.done()
      return ret
    } catch(e) {
      if(timing) timing.fail()
      throw e
    }
  }

  async _decryptImage(identityId, blob, imageTimestamp, timing) {
    if(blob === null) {
      return null
    }
    if(typeof blob === 'undefined') {
      return blob
    }

    // NC1 dispatch (AWS-PORT chunk 4): the envelope is self-describing (magic
    // + AAD carries deviceId/epoch/field), so route by sniffing the payload —
    // BEFORE any Virgil init or passcode gating, since NC1 accounts may have
    // no Virgil state at all. Anything without the magic (Virgil ciphertext,
    // S3 error XML, plaintext) falls through to the untouched Virgil path.
    const bytes = new Uint8Array(await blob.arrayBuffer())
    if(Nc1Decryptor.isNc1Bytes(bytes)) {
      if(timing) timing.note('nc1', bytes.length)
      return Nc1Decryptor.decryptImageBlob(this.accountId, bytes, timing)
    }
    if(timing) timing.note('legacy', bytes.length)

    await this.initPromise

    // If this account's data is encrypted but we have no usable local private
    // key, the viewer needs to enter their passcode. Detect this up front
    // instead of attempting to decrypt and string-matching Virgil's error.
    if(this.e2eeEnabled && !this.hasLocalKey) {
      throw new E2EEPasscodeRequiredError(this.accountId)
    }

    let cardToVerify = await this._resolveSendersCard(identityId, 'getSendersCard (image)')

    let decryptFunction = async () => {
      let error
      if(timing) timing.cryptoBegin()
      let ret = await this.eThree.authDecryptFile(blob, cardToVerify).catch(err => {
      // let ret = await this.eThree.authDecrypt(blob, cardToVerify).catch(err => {
        error = err
        console.log(error)
      })
      if(timing) timing.cryptoEnd()
      if(error) throw error
      return ret
    }

    let decryptedBlob

    try {
      let error
      decryptedBlob = await decryptFunction().catch(err => {
        error = err
      })
      if(error) throw error

      if(this.maxTimestampWithSucccessfulDecryption < imageTimestamp) {
        this.maxTimestampWithSucccessfulDecryption = imageTimestamp
      }

      E2EEStatus.reportSuccess()

    } catch(e) {
      console.log(e)

      if(this.maxTimestampWithSucccessfulDecryption <= imageTimestamp && Date.now() - this.lastRefreshedCardToVerifyCard > 60 * 1000) {
        this.lastRefreshedCardToVerifyCard = Date.now()
        console.log('invalidateCard')
        //could be a stale card, reload...  Unlikely? though.
        E2EECardCache.invalidateCard(identityId)
        cardToVerify = await this._resolveSendersCard(identityId, 'getSendersCard (image retry)')
        let error
        decryptedBlob = await decryptFunction().catch(err => {
          error = err
        })
        if(error) throw error
        E2EEStatus.reportSuccess()
      } else {
        throw e
      }
    }

    return decryptedBlob
  }

  async decryptString(identityId, str, timestamp) {
    const timing = DecryptTiming.start('string')
    try {
      const ret = await this._decryptString(identityId, str, timestamp, timing)
      if(timing) timing.done()
      return ret
    } catch(e) {
      if(timing) timing.fail()
      throw e
    }
  }

  async _decryptString(identityId, str, timestamp, timing) {
    if(str === null) {
      return null
    }
    if(typeof str === 'undefined') {
      return str
    }

    // NC1 dispatch — see decryptImage. "NC1" base64-encodes to "TkMx", so
    // NC1 string envelopes are recognizable without decoding; Virgil
    // ciphertext never starts with that prefix.
    if(Nc1Decryptor.isNc1String(str)) {
      if(timing) timing.note('nc1', str.length)
      return Nc1Decryptor.decryptStringEnvelope(this.accountId, str, timing)
    }
    if(timing) timing.note('legacy', typeof str === 'string' ? str.length : null)

    await this.initPromise

    if(this.e2eeEnabled && !this.hasLocalKey) {
      throw new E2EEPasscodeRequiredError(this.accountId)
    }

    let cardToVerify = await this._resolveSendersCard(identityId, 'getSendersCard (string)')

    let decryptFunction = async () => {
      let error
      if(timing) timing.cryptoBegin()
      let ret = await this.eThree.authDecrypt(str, cardToVerify).catch(err => {
        error = err
        console.log(error)
      })
      if(timing) timing.cryptoEnd()
      if(error) throw error
      return ret
    }

    let decryptedString

    try {
      let error
      decryptedString = await decryptFunction().catch(err => {
        error = err
      })
      if(error) throw error

      if(this.maxTimestampWithSucccessfulDecryption < timestamp) {
        this.maxTimestampWithSucccessfulDecryption = timestamp
      }

      E2EEStatus.reportSuccess()

    } catch(e) {
      console.log(e)
      if(this.maxTimestampWithSucccessfulDecryption <= timestamp && Date.now() - this.lastRefreshedCardToVerifyCard > 60 * 1000) {
        this.lastRefreshedCardToVerifyCard = Date.now()
        //could be a stale card, reload...  Unlikely? though.
        E2EECardCache.invalidateCard(identityId)
        cardToVerify = await this._resolveSendersCard(identityId, 'getSendersCard (string retry)')
        let error
        decryptedString = await decryptFunction().catch(err => {
          error = err
        })
        if(error) throw error
        E2EEStatus.reportSuccess()
      } else {
        throw e
      }
    }

    return decryptedString
  }

  async unregisterAccountAndAllCards() {
    await this.initPromise
    console.log('unregisterUser')
    try {
      await this.eThree.unregisterWithMultipleCards(() => { return true; }).catch(err => {
        console.log('error unregisterWithMultipleCards')
        console.log(err)
      })
    } catch(e) {
      console.log('unregisterWithMultipleCards')
      console.log(e)
    }

    try {
      await this.eThree.resetPrivateKeyBackup().catch(err => {
        console.log('error resetPrivateKeyBackup')
        console.log(err)
      })
    } catch(e) {
      console.log('resetPrivateKeyBackup')
      console.log(e)
    }

    try {
      await this.eThree.cleanup().catch(err => {
        console.log('error cleanup')
        console.log(err)
      })
    } catch(e) {
      console.log('cleanup')
      console.log(e)
    }
  }

  async unregisterUser() {
    await this.initPromise
    console.log('unregisterUser')

    // await this.eThree.unregisterWithMultipleCards(() => { return true; }).catch(err => {
    //   console.log('error unregisterWithMultipleCards')
    //   console.log(err)
    // })

    // await this.eThree.unregisterWithMultipleCards(accountCardFilter).catch(err => {
    //   console.log('error unregisterWithMultipleCards')
    //   console.log(err)
    // })
    await this.eThree.resetPrivateKeyBackup().catch(err => {
      console.log('error resetPrivateKeyBackup')
      console.log(err)
    })
    await this.eThree.cleanup().catch(err => {
      console.log('error cleanup')
      console.log(err)
    })
  }

  async registerUser(password) {
    await this.initPromise

    try {
      await this.unregisterAccountAndAllCards()
    } catch(e) {
      console.log(`error unregistering right before registering`)
      console.log(e)
    }

    await this.eThree.registerWithMultipleCards(
      undefined,
      accountCardFilter,
      {
        accountCard: "true"
      }
    )
    let accountCardId = await this._getAccountCardId()
    this._setCachedAccountCardId(accountCardId)

    this.e2eeEnabled = true
    this.hasLocalKey = await this._hasPrivateKeyThatIsValid()
    E2EEEvictionTracker.recordSetup(this.accountId).catch(err => console.log('recordSetup error', err))
    console.log('registerUser complete, starting backup')
    try {
      await this.backup(password)
    } catch(e) {
      console.log(e)
      await this.unregisterAccountAndAllCards()
      alert(`An error occurred registering your e2ee passcode.  Please try again later.  If this repeatedly occurs, contact support@${process.env.VUE_APP_DOMAIN}`)
      location.reload()
    }
    console.log('backup successful')
  }

  // getPasswordFromLocalStorage() {
  //   return localStorage.getItem(`virgil-password.${this.virgilAccountIdentityId}`)
  // }

  async backup(password) {
    await this.initPromise
    await this.eThree.backupPrivateKey(password)
  }

  async changeBackupPassword(oldPassword, newPassword) {
    await this.initPromise
    return await this.eThree.changePassword(oldPassword, newPassword)
  }

  async _getAccountCardId() {
    let error
    let result = await this.eThree.findUsersWithCardFilter(
      this.virgilAccountIdentityId,
      accountCardFilter
    ).catch(err => {
      error = err
    })
    if(error) throw error
    return result.id
  }

  async downloadPrivateKey(password) {
    await this.initPromise
    let accountCardId = await this._getAccountCardId()
    const hasLocalPrivateKey = await this.eThree.hasLocalPrivateKey();
    if (hasLocalPrivateKey) {
      await this.eThree.cleanup()
    }

    await this.eThree.restorePrivateKey(password);
    this.hasLocalKey = await this.eThree.hasLocalPrivateKey()

    let accountCardIdAfterRestoring = await this._getAccountCardId()

    if(accountCardId !== accountCardIdAfterRestoring) {
      throw new Error('Encryption key changed while attempting to restore the key, please try again.')
    }

    this._setCachedAccountCardId(accountCardId)
    E2EEEvictionTracker.recordSetup(this.accountId).catch(err => console.log('recordSetup error', err))
  }

  // async unregisterUser() {
  //   if(!this.forAccountId) {
  //     //should be done on backend when cancelling subscription
  //     //can't unregister unless it's for your own account
  //   }
  // }


  async rotatePrivateKey() {
    await this.initPromise
    const hasLocalPrivateKey = await this.eThree.hasLocalPrivateKey()

    if(hasLocalPrivateKey) {
      // await this.eThree.resetPrivateKeyBackup()
      // await this.eThree.cleanup()
      console.log('resetPrivateKeyBackup')
      await this.eThree.resetPrivateKeyBackup().catch(err => {
        console.log('resetPrivateKeyBackup error')
        console.log(err)
      })
      await this.eThree.cleanup()
    }

    await this.eThree.rotatePrivateKeyWithMultipleCards(
      accountCardFilter,
      {
        accountCard: "true"
      }
    )
  }

  async getAllCards() {
    let cards = []
    await this.eThree.findUsersWithCardFilter(
      this.virgilAccountIdentityId,
      (card) => {
        cards.push(card)
        return false
      }
    ).catch(() => {

    })
    return cards
  }

  async hasE2eEnabled() {
    try {
      let error
      console.log('looking for ' + this.virgilAccountIdentityId)
      let result = await this.eThree.findUsersWithCardFilter(
        this.virgilAccountIdentityId,
        accountCardFilter
      ).catch(err => {
        console.log('throwing!')
        error = err
      })
      if(error) throw error

      // let count = 0;
      // for

      return true
    } catch(e) {
      console.log('hasE2eEnabled error: ' + (e && (e.name || e.message) || 'unknown'))
      return false
    }
  }

  async debug() {
    await this.initPromise
    let result = await this.eThree.resetPrivateKeyBackup()
    console.log(result)
    result = await this.eThree.unregister(true)
    await this.eThree.cleanUp()

    console.log('success')
    // let result = eThree.rotatePrivateKey()
    // let result = eThree.findUsers(this.virgilAccountIdentityId)
  }

  async removePrivateKeyFromLocalStorage() {
    await this.initPromise
    //if on public device.
    await this.eThree.cleanUp()
  }

  async deletePrivateKey() {

    await this.eThree.resetPrivateKeyBackup().catch(err => {
      console.log('resetPrivateKeyBackup error')
      console.log(err)
    })

    const hasLocalPrivateKey = await this.eThree.hasLocalPrivateKey()

    if(hasLocalPrivateKey) {
      // await this.eThree.resetPrivateKeyBackup()
      // await this.eThree.cleanup()
      console.log('resetPrivateKeyBackup')
      await this.eThree.cleanup()
    }
  }
}

export default E2EEHelper